import { type MutableRefObject, useEffect, useState } from "react";

type AudioState = "counting" | "playing" | "not-started" | "finished" | "error";

const INTERLUDE_SRC = "/audio/mezi-stopa.mp3";

export const useAudioState = (
  audioRef: MutableRefObject<HTMLAudioElement | null>,
  audioSrc: string,
  countdownFrom = 5,
) => {
  const [state, setState] = useState<AudioState>("counting");
  const [countdown, setCountdown] = useState(countdownFrom);
  const [partIndex, setPartIndex] = useState(0);

  // AIDEV-NOTE: TD-450 specifies double playback for English listening only, but it runs for any assignmentAudio.
  // Only AJ has real recordings (stage 2026-09-30: 119 AJ, 2 CJ = 2 s silence tests) and AssignmentType
  // in GraphQL does not expose the subject, so restricting it would need a backend change.
  const parts = [audioSrc, INTERLUDE_SRC, audioSrc];

  useEffect(() => {
    if (state === "counting" && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(c => c - 1);
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    } else if (state === "counting" && countdown === 0) {
      setState("playing");
    }
  }, [state, countdown]);

  // AIDEV-NOTE: all parts run through ONE <audio> element (only its src changes) — autoplay policy lets an
  // element that already played continue, while a freshly mounted one may be blocked mid-sequence.
  useEffect(() => {
    if (state === "playing" && audioRef.current) {
      const audio = audioRef.current;
      audio.volume = 1;
      audio.play().catch(e => {
        setState("not-started");
        console.error("Playback failed:", e);
      });
    }
  }, [state, partIndex]);

  const handleAudioEnded = () => {
    if (partIndex < parts.length - 1) {
      setPartIndex(partIndex + 1);
    } else {
      setState("finished");
    }
  };

  const handleStartManually = () => {
    if (audioRef.current) {
      audioRef.current
        .play()
        .then(() => {
          setState("playing");
        })
        .catch(e => {
          setState("error");
          console.error("Playback failed:", e);
        });
    }
  };

  return { state, countdown, currentSrc: parts[partIndex], handleAudioEnded, handleStartManually };
};
